import React, { useState, useEffect } from 'react';
import {
  FaUser, FaEnvelope, FaLock, FaEye, FaEyeSlash, FaSave, FaTimes, FaMagic, FaCheck, FaCopy,
} from 'react-icons/fa';
import Input from '../../../components/Input';
import Button from '../../../components/Button';

const UPPER   = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const LOWER   = 'abcdefghijklmnopqrstuvwxyz';
const DIGITS  = '0123456789';
const SPECIAL = '!@#$%&*()-_=+[]{}|;:,.<>?';
const ALL     = UPPER + LOWER + DIGITS + SPECIAL;
const ri = (max) => { const a = new Uint32Array(1); crypto.getRandomValues(a); return a[0] % max; };
const generatePassword = (len = 18) => {
  const req = [UPPER[ri(UPPER.length)], LOWER[ri(LOWER.length)], DIGITS[ri(DIGITS.length)], SPECIAL[ri(SPECIAL.length)]];
  const rest = Array.from({ length: len - 4 }, () => ALL[ri(ALL.length)]);
  const all = [...req, ...rest];
  for (let i = all.length - 1; i > 0; i--) { const j = ri(i + 1); [all[i], all[j]] = [all[j], all[i]]; }
  return all.join('');
};

const ModalAdminUser = ({ open, onClose, onSave, editData }) => {
  const isEdit = !!editData;

  const [form, setForm] = useState({ full_name: '', email: '', password: '', confirmPassword: '' });
  const [show, setShow] = useState({ password: false, confirm: false });
  const [loading, setLoading] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const [copied, setCopied] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      setForm(editData
        ? { full_name: editData.full_name || '', email: editData.email || '', password: '', confirmPassword: '' }
        : { full_name: '', email: '', password: '', confirmPassword: '' }
      );
      setErrors({});
      setSuggestion(null);
    }
  }, [open, editData]);

  const applySuggestion = () => {
    const p = generatePassword();
    setSuggestion(p);
    setForm(f => ({ ...f, password: p, confirmPassword: p }));
    setShow({ password: true, confirm: true });
    setCopied(false);
  };

  const copy = () => {
    if (!suggestion) return;
    navigator.clipboard.writeText(suggestion).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };

  const validate = () => {
    const e = {};
    if (!form.full_name.trim())  e.full_name = 'Requerido';
    if (!form.email.trim())      e.email = 'Requerido';
    if (!isEdit) {
      if (!form.password)                          e.password = 'Requerido';
      else if (form.password.length < 8)           e.password = 'Mínimo 8 caracteres';
      if (form.password !== form.confirmPassword)  e.confirmPassword = 'No coinciden';
    } else if (form.password && form.password !== form.confirmPassword) {
      e.confirmPassword = 'No coinciden';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await onSave(form, editData);
      onClose();
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div
        className="relative w-full max-w-lg rounded-2xl overflow-hidden"
        style={{ backgroundColor: 'rgb(var(--panel))', border: '1px solid rgb(var(--fg) / 0.08)' }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-4"
          style={{ borderBottom: '1px solid rgb(var(--fg) / 0.06)' }}
        >
          <div>
            <p className="text-sm font-semibold" style={{ color: 'rgb(var(--fg))' }}>
              {isEdit ? 'Editar administrador' : 'Nuevo administrador'}
            </p>
            <p className="text-xs mt-0.5" style={{ color: 'rgb(var(--fg-faint))' }}>
              {isEdit ? 'Deja la contraseña vacía para no cambiarla' : 'Crea una cuenta de acceso al panel'}
            </p>
          </div>
          <button type="button" onClick={onClose}
            className="h-8 w-8 rounded-lg flex items-center justify-center transition-colors"
            style={{ color: 'rgb(var(--fg-faint))', backgroundColor: 'rgb(var(--fg) / 0.04)' }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgb(var(--fg) / 0.08)'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'rgb(var(--fg) / 0.04)'}>
            <FaTimes size={12} />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <Input label="Nombre completo" name="full_name" type="text" size="lg"
            value={form.full_name} leftIcon={<FaUser />} error={errors.full_name}
            onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} />

          <Input label="Correo electrónico" name="email" type="email" size="lg"
            value={form.email} leftIcon={<FaEnvelope />} error={errors.email}
            onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />

          {/* Password section */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-semibold uppercase tracking-widest" style={{ color: 'rgb(var(--fg-faint))' }}>
                {isEdit ? 'Nueva contraseña (opcional)' : 'Contraseña'}
              </p>
              <button type="button" onClick={applySuggestion}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all"
                style={{ backgroundColor: 'rgba(167,139,250,0.1)', border: '1px solid rgba(167,139,250,0.25)', color: '#A78BFA' }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(167,139,250,0.18)'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'rgba(167,139,250,0.1)'}>
                <FaMagic size={9} /> Generar
              </button>
            </div>

            {suggestion && (
              <div className="flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl mb-3"
                style={{ backgroundColor: 'rgba(167,139,250,0.07)', border: '1px solid rgba(167,139,250,0.2)' }}>
                <p className="text-xs font-mono font-semibold tracking-wider truncate select-all" style={{ color: '#C4B5FD' }}>
                  {suggestion}
                </p>
                <button type="button" onClick={copy}
                  className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs flex-shrink-0 transition-all"
                  style={{ backgroundColor: copied ? 'rgba(74,222,128,0.12)' : 'rgba(167,139,250,0.15)', color: copied ? '#4ADE80' : '#A78BFA', border: `1px solid ${copied ? 'rgba(74,222,128,0.3)' : 'rgba(167,139,250,0.25)'}` }}>
                  {copied ? <FaCheck size={10} /> : <FaCopy size={10} />}
                  {copied ? 'Copiada' : 'Copiar'}
                </button>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <Input label="Contraseña" name="password"
                type={show.password ? 'text' : 'password'} size="lg"
                value={form.password} leftIcon={<FaLock />} error={errors.password}
                rightIcon={
                  <button type="button" tabIndex={-1} onClick={() => setShow(s => ({ ...s, password: !s.password }))}
                    style={{ color: 'rgb(var(--fg-faint))', background: 'none', border: 'none', cursor: 'pointer', display: 'flex' }}>
                    {show.password ? <FaEyeSlash size={13} /> : <FaEye size={13} />}
                  </button>
                }
                onChange={e => { setForm(f => ({ ...f, password: e.target.value })); setSuggestion(null); }} />
              <Input label="Confirmar" name="confirmPassword"
                type={show.confirm ? 'text' : 'password'} size="lg"
                value={form.confirmPassword} leftIcon={<FaLock />} error={errors.confirmPassword}
                rightIcon={
                  <button type="button" tabIndex={-1} onClick={() => setShow(s => ({ ...s, confirm: !s.confirm }))}
                    style={{ color: 'rgb(var(--fg-faint))', background: 'none', border: 'none', cursor: 'pointer', display: 'flex' }}>
                    {show.confirm ? <FaEyeSlash size={13} /> : <FaEye size={13} />}
                  </button>
                }
                onChange={e => { setForm(f => ({ ...f, confirmPassword: e.target.value })); setSuggestion(null); }} />
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" size="sm" onClick={onClose}>Cancelar</Button>
            <Button type="submit" variant="primary" size="sm"
              leftIcon={<FaSave size={11} />} disabled={loading}>
              {loading ? 'Guardando…' : isEdit ? 'Guardar cambios' : 'Crear administrador'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ModalAdminUser;
